import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { axiosClient } from '../api/proxy/AxiosClient';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login } from '../redux/authSlice';
import { updateSettings } from '../redux/settingsSlice';
import styled from 'styled-components';
import { Link } from '@mui/material';
import Popup from '../components/Popup';

const LoginContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const DemoContainer = styled.div`
    display: flex;
    gap: 30px;
    justify-content: center;
    width: 100%;
`;

const InfoBlock = styled.div`
    max-width: 700px;
    text-align: center;
    font-size: 14px;
    line-height: 1.6;
    color: white;
    background:rgb(78, 123, 190);
    border: 1px solid #e1e6ef;
    border-radius: 6px;
    padding: 16px 24px;
`;

const LoginForm = styled.div`
    background: white;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    width: 300px;
`;

const Title = styled.h2`
    color: #333;
    text-align: center;
    margin-bottom: 20px;
`;

const InputFieldWrapper = styled.div`
    position: relative;
    margin: 10px 0;
`;

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    padding-top: 40px;
`;

const InputField = styled.input`
    width: 100%;
    box-sizing: border-box;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 16px;

    &:focus {
        border-color: #6e8fbd;
        outline: none;
    }

    &:read-only {
        background-color: #f2f2f2;
        color: #888;
        cursor: not-allowed;
    }
`;

const EyeButton = styled.button`
    position: absolute;
    top: 50%;
    right: 5px;
    transform: translateY(-50%);
    border: none;
    background: none;
    cursor: pointer;
    font-size: 18px;
    color: #6e8fbd;
`;

const LoginButton = styled.button`
    width: 100%;
    padding: 10px;
    background-color: #6e8fbd;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
        background-color: #5a7ca1;
    }
`;

const Login = () => {
    const [needActivation, setIsNeedActivation] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    // const [error, setError] = useState('');
    const [resetPassword, setResetPassword] = useState(false);
    const [resetEmailSentNotification, setResetEmailSentNotification] = useState(false);
    const [activationSuccess, setActivationSuccess] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (searchParams.has('activated')) {
            setActivationSuccess(true);
            const next = new URLSearchParams(searchParams);
            next.delete('activated');
            setSearchParams(next, { replace: true });
        }
        if (searchParams.has('email')) {
            setUsername(searchParams.get('email'));
            const next = new URLSearchParams(searchParams);
            next.delete('email');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const [demoStudentUsername] = useState('demo.student@k2e.space');
    const [demoStudentPassword] = useState('password');
    const [demoTeacherUsername] = useState('demo.teacher@k2e.space');
    const [demoTeacherPassword] = useState('password');

    function getRedirectUrl(userType) {
        switch (userType) {
            case 'superuser':
            case 'admin':
                return '/users';
            case 'outwrite':
                return '/outwrite/tasks';
            case 'outwrite_learner':
                return '/outwrite/learn';
            default:
                return `/${userType}/summary`;
           }
    }
    const handleLogin = async (e) => {
        e.preventDefault();

        axiosClient.post(`/auth/login/`, {
            username: username,
            password: password,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }).then((response) => {
            // setError('');
            const { settings = [], type: userType } = response.data.user || {};
            const { needActivation, passwordExpired } = response.data;
            
            setResetPassword(false);
            if (needActivation) {
                setIsNeedActivation(true);
                return;
            }
            
            dispatch(login(response.data));
            dispatch(updateSettings(settings));
            
            if (passwordExpired) {
                navigate('/change-password', { replace: true });
                return;
            }
            
            navigate(getRedirectUrl(userType), { replace: true });
        
        }).catch(error => {
            if (error.statusCode === 401) {
                setResetPassword(true);
            } else {
                // setError(error.message);
            }
        });
    };

    const handleResetInit = () => {
        axiosClient.post(`/auth/password/init/`, {
            username: username
        }).then((response) => {
            setResetPassword(false);
            setResetEmailSentNotification(true);
        }).catch(error => {
            // setError(error.message);
        });
    }

    const handleDemoTeacherLogin = async (e) => {
        e.preventDefault();

        axiosClient.post(`/auth/login/`, {
            username: demoTeacherUsername,
            password: demoTeacherPassword,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }).then((response) => {
            console.log('Demo Teacher Login successful:', response.data);
            dispatch(login(response.data));
            navigate(`/teacher/summary`, { replace: true });
        }).catch(error => {});
    };

    const handleDemoStudentLogin = async (e) => {
        e.preventDefault();

        axiosClient.post(`/auth/login/`, {
            username: demoStudentUsername,
            password: demoStudentPassword,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }).then((response) => {
            console.log('Demo Student Login successful:', response.data);
            dispatch(login(response.data));
            navigate(`/student/summary`, { replace: true });
        }).catch(error => {});
    };

    return (
        <>
        <PageWrapper>
    
            <LoginContainer>
                <LoginForm>
                    <Title>Log In</Title>
                    <form onSubmit={handleLogin}>
                        <InputFieldWrapper>
                            <InputField
                                type="text"
                                placeholder="Email"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                autoFocus
                            />
                        </InputFieldWrapper>
                        {/* {error && (
                            <p>{error}</p>
                        )} */}
                        <InputFieldWrapper>
                            <InputField
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <EyeButton
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? "🙈" : "👁️"}
                            </EyeButton>
                        </InputFieldWrapper>
    
                        <LoginButton type="submit">Submit</LoginButton>
                    </form>
    
                    {needActivation && (
                        <p>Please activate your account via the link sent to your email.</p>
                    )}
                    {resetPassword && (
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => { handleResetInit(); }}
                        >
                        Reset Password
                      </Link>
                    )}

                </LoginForm>
            </LoginContainer>
    
            <InfoBlock>
                This system is currently operating in beta mode.
                <br />
                To help you explore its core features, demo accounts with limited
                functionality are available below. These accounts are intended for
                evaluation purposes only and may not reflect the full capabilities of
                the platform.
            </InfoBlock>


            <DemoContainer>
    
                <LoginForm>
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        handleDemoTeacherLogin(e);
                    }}>
                        <InputFieldWrapper>
                            <InputField
                                type="text"
                                value={demoTeacherUsername}
                                readOnly
                            />
                        </InputFieldWrapper>
    
                        <InputFieldWrapper>
                            <InputField
                                type="password"
                                value={demoTeacherPassword}
                                readOnly
                            />
                        </InputFieldWrapper>
    
                        <LoginButton type="submit">
                            Login as Teacher
                        </LoginButton>
                    </form>
                </LoginForm>
    
                <LoginForm>
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        handleDemoStudentLogin(e);
                    }}>
                        <InputFieldWrapper>
                            <InputField
                                type="text"
                                value={demoStudentUsername}
                                readOnly
                            />
                        </InputFieldWrapper>
    
                        <InputFieldWrapper>
                            <InputField
                                type="password"
                                value={demoStudentPassword}
                                readOnly
                            />
                        </InputFieldWrapper>
    
                        <LoginButton type="submit">
                            Login as Student
                        </LoginButton>
                    </form>
                </LoginForm>
    
            </DemoContainer>

        </PageWrapper>

            
        <Popup
            open={resetEmailSentNotification}
            title="Reset Password Email Sent"
            text={`Please check your email [${username}] for instructions to reset your password.`}
            onClose={() => setResetEmailSentNotification(false)}
        />
        <Popup
            open={activationSuccess}
            title="Account activated"
            text="Your account is active. You can log in with your email and password."
            onClose={() => setActivationSuccess(false)}
        />
        </>
    );
   
};

export default Login;